import { memo, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGlobalSettings } from 'providers';
import { useLocationParams } from 'hooks';
import type { TwitterFormValues } from '../validation/twitter.schema';
import { twitterSchema } from '../validation/twitter.schema';
import { getTwitterFormDefaultValues } from '../validation/defaultValues';
import { idsFromSelection } from 'utils';
import TwitterForm from './TwitterForm';

const TwitterFormProvider = memo(() => {
  const { selectedApp } = useGlobalSettings();
  const {
    app: appParam,
    adAccount: adAccountParam,
    flatCreatives: flatCreativesParam,
    creativesFormat: creativesFormatParam,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    filterCreativesByName: filterCreativesByNameParam,
    filterCampaignsByName: filterCampaignsByNameParam,
    filterAdSetsByName: filterAdSetsByNameParam,
    adSettingsFlow: adSettingsFlowParam,
  } = useLocationParams();

  const defaultValues = useMemo<TwitterFormValues>(
    () => ({
      ...getTwitterFormDefaultValues(appParam ?? selectedApp ?? ''),
      accountId: adAccountParam ?? '',
      selectedCreatives: creativesFormatParam === 'flat' ? flatCreativesParam : [],
      filterCreativeByName: filterCreativesByNameParam,
      selectedCampaigns: idsFromSelection(selectedCampaignsParam),
      filterCampaignsByName: filterCampaignsByNameParam,
      selectedLineItems: idsFromSelection(selectedAdSetsParam),
      filterLineItemsByName: filterAdSetsByNameParam,
      adSettings: adSettingsFlowParam,
    }),
    [
      adAccountParam,
      adSettingsFlowParam,
      appParam,
      creativesFormatParam,
      filterAdSetsByNameParam,
      filterCampaignsByNameParam,
      filterCreativesByNameParam,
      flatCreativesParam,
      selectedAdSetsParam,
      selectedApp,
      selectedCampaignsParam,
    ],
  );

  const formMethods = useForm<TwitterFormValues>({
    resolver: yupResolver(twitterSchema),
    defaultValues,
    mode: 'onChange',
  });

  return (
    <FormProvider {...formMethods}>
      <TwitterForm />
    </FormProvider>
  );
});

TwitterFormProvider.displayName = 'TwitterFormProvider';

export default TwitterFormProvider;
